import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import type { PageResult } from '../../shared/data/types';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { paths } from '../../routes/paths';
import { toPageError } from '../partners/PartnerListPage';
import { GameDialog } from './GameDialog';
import { GameStatusDialog } from './GameStatusDialog';
import { GameTypeDialog } from './GameTypeDialog';
import { defaultGameListQuery, defaultGameTypeListQuery, gamesRepositoryToken } from './repository';
import type {
  GameDetail,
  GameFilters,
  GameListQuery,
  GameSummary,
  GameTypeDetail,
  GameTypeFilters,
  GameTypeListQuery,
  GameTypeSummary,
  MasterStatus,
} from './types';
import { normalizeMasterCode } from './types';

export type GameMasterView = 'list' | 'gameTypeDetail' | 'gameDetail';

export function GameMasterPage({
  dataSource,
  view = 'list',
}: {
  readonly dataSource: DataSource;
  readonly view?: GameMasterView;
}) {
  return view === 'list' ? (
    <GameMasterList dataSource={dataSource} />
  ) : (
    <MasterDetail dataSource={dataSource} kind={view === 'gameTypeDetail' ? 'gameType' : 'game'} />
  );
}

function GameMasterList({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(gamesRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [tab, setTab] = useState<'gameTypes' | 'games'>('gameTypes');
  const [gameTypeQuery, setGameTypeQuery] = useState(defaultGameTypeListQuery);
  const [gameQuery, setGameQuery] = useState(defaultGameListQuery);
  const [refreshKey, setRefreshKey] = useState(0);
  const [gameTypeOpen, setGameTypeOpen] = useState(false);
  const [gameOpen, setGameOpen] = useState(false);
  const gameTypes = useResource(
    (signal) => repository.listGameTypes(gameTypeQuery, signal),
    [gameTypeQuery, refreshKey, repository],
    (page) => page.items.length === 0,
  );
  const games = useResource(
    (signal) => repository.listGames(gameQuery, signal),
    [gameQuery, refreshKey, repository],
    (page) => page.items.length === 0,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('GAME_MANAGE');
  const refresh = () => setRefreshKey((key) => key + 1);

  const gameTypeColumns = useMemo<readonly GridColDef<GameTypeSummary>[]>(
    () => [
      {
        field: 'code',
        headerName: t('games.fields.code'),
        minWidth: 150,
        renderCell: ({ row }) => <Link to={`/game-types/${row.id}`}>{row.code}</Link>,
      },
      { field: 'name', headerName: t('games.fields.name'), flex: 1, minWidth: 200 },
      {
        field: 'status',
        headerName: t('games.fields.status'),
        minWidth: 130,
        renderCell: ({ row }) => <MasterStatusChip status={row.status} />,
      },
      {
        field: 'updatedAt',
        headerName: t('games.fields.updatedAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
    ],
    [language, t],
  );
  const gameColumns = useMemo<readonly GridColDef<GameSummary>[]>(
    () => [
      {
        field: 'code',
        headerName: t('games.fields.code'),
        minWidth: 170,
        renderCell: ({ row }) => <Link to={`/games/${row.id}`}>{row.code}</Link>,
      },
      { field: 'name', headerName: t('games.fields.name'), flex: 1, minWidth: 200 },
      {
        field: 'gameType',
        headerName: t('games.fields.gameType'),
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Link to={`/game-types/${row.gameType.id}`}>{row.gameType.code}</Link>
        ),
      },
      {
        field: 'status',
        headerName: t('games.fields.status'),
        minWidth: 130,
        renderCell: ({ row }) => <MasterStatusChip status={row.status} />,
      },
      {
        field: 'updatedAt',
        headerName: t('games.fields.updatedAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
    ],
    [language, t],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            {canManage ? (
              <Button
                onClick={() => (tab === 'gameTypes' ? setGameTypeOpen(true) : setGameOpen(true))}
                variant="contained"
              >
                {t(
                  tab === 'gameTypes' ? 'games.actions.createGameType' : 'games.actions.createGame',
                )}
              </Button>
            ) : null}
          </>
        }
        subtitle={t('games.subtitle')}
        title={t('games.title')}
      />
      <Tabs
        aria-label={t('games.tabs.label')}
        onChange={(_event, value: 'gameTypes' | 'games') => setTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        value={tab}
        variant="scrollable"
      >
        <Tab label={t('games.tabs.gameTypes')} value="gameTypes" />
        <Tab label={t('games.tabs.games')} value="games" />
      </Tabs>
      {tab === 'gameTypes' ? (
        <>
          <GameTypeFiltersForm
            onApply={(filters) => setGameTypeQuery((current) => ({ ...current, filters, page: 1 }))}
            value={gameTypeQuery.filters}
          />
          <PageState
            emptyMessage={t('games.empty.gameTypes')}
            error={toPageError(gameTypes.error, t('games.errors.loadGameTypes'))}
            onRetry={refresh}
            status={gameTypes.status}
          >
            {gameTypes.data === null ? null : (
              <MasterGrid
                columns={gameTypeColumns}
                label={t('games.tables.gameTypes')}
                onQueryChange={setGameTypeQuery}
                page={gameTypes.data}
                query={gameTypeQuery}
              />
            )}
          </PageState>
        </>
      ) : (
        <>
          <GameFiltersForm
            gameTypes={gameTypes.data?.items ?? []}
            onApply={(filters) => setGameQuery((current) => ({ ...current, filters, page: 1 }))}
            value={gameQuery.filters}
          />
          <PageState
            emptyMessage={t('games.empty.games')}
            error={toPageError(games.error, t('games.errors.loadGames'))}
            onRetry={refresh}
            status={games.status}
          >
            {games.data === null ? null : (
              <MasterGrid
                columns={gameColumns}
                label={t('games.tables.games')}
                onQueryChange={setGameQuery}
                page={games.data}
                query={gameQuery}
              />
            )}
          </PageState>
        </>
      )}
      <GameTypeDialog
        onClose={() => setGameTypeOpen(false)}
        onSaved={refresh}
        open={gameTypeOpen}
      />
      <GameDialog onClose={() => setGameOpen(false)} onSaved={refresh} open={gameOpen} />
    </Box>
  );
}

function MasterGrid<
  Row extends GameTypeSummary | GameSummary,
  Query extends GameTypeListQuery | GameListQuery,
>({
  columns,
  label,
  onQueryChange,
  page,
  query,
}: {
  readonly columns: readonly GridColDef<Row>[];
  readonly label: string;
  readonly onQueryChange: (query: Query) => void;
  readonly page: PageResult<Row>;
  readonly query: Query;
}) {
  const gridQuery = { ...query, page: query.page - 1 };
  return (
    <Box aria-label={label} role="region" sx={{ maxWidth: '100%', overflowX: 'auto' }} tabIndex={0}>
      <ServerDataGrid
        columns={columns}
        disableRowSelectionOnClick
        onQueryChange={(next) => onQueryChange({ ...next, page: next.page + 1 } as Query)}
        page={page}
        query={gridQuery}
        sx={{ minWidth: 820 }}
      />
    </Box>
  );
}

function GameTypeFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: GameTypeFilters) => void;
  readonly value: GameTypeFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<GameTypeFilters>({ defaultValues: value });
  return (
    <ListFilterPanel
      applyLabel={t('games.actions.applyGameTypeFilters')}
      ariaLabel={t('filterPanel.label', { page: t('games.tabs.gameTypes') })}
      onClear={() => {
        reset(defaultGameTypeListQuery.filters);
        onApply(defaultGameTypeListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          code: normalizeMasterCode(filters.code),
          name: filters.name.trim(),
          status: filters.status,
        }),
      )}
      primaryFields={
        <>
          <TextField label={t('games.filters.gameTypeCode')} size="small" {...register('code')} />
          <TextField label={t('games.filters.name')} size="small" {...register('name')} />
          <StatusFilter label={t('games.filters.status')} registration={register('status')} />
        </>
      }
    />
  );
}

function GameFiltersForm({
  gameTypes,
  onApply,
  value,
}: {
  readonly gameTypes: readonly GameTypeSummary[];
  readonly onApply: (filters: GameFilters) => void;
  readonly value: GameFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<GameFilters>({ defaultValues: value });
  return (
    <ListFilterPanel
      applyLabel={t('games.actions.applyGameFilters')}
      ariaLabel={t('filterPanel.label', { page: t('games.tabs.games') })}
      onClear={() => {
        reset(defaultGameListQuery.filters);
        onApply(defaultGameListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          gameTypeId: filters.gameTypeId,
          code: normalizeMasterCode(filters.code),
          name: filters.name.trim(),
          status: filters.status,
        }),
      )}
      primaryFields={
        <>
          <TextField
            defaultValue=""
            label={t('games.filters.gameType')}
            select
            size="small"
            {...register('gameTypeId')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {gameTypes.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.code}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('games.filters.gameCode')} size="small" {...register('code')} />
          <TextField label={t('games.filters.name')} size="small" {...register('name')} />
          <StatusFilter label={t('games.filters.status')} registration={register('status')} />
        </>
      }
    />
  );
}

function StatusFilter({
  label,
  registration,
}: {
  readonly label: string;
  readonly registration: ReturnType<ReturnType<typeof useForm<GameTypeFilters>>['register']>;
}) {
  const { t } = useTranslation();
  return (
    <TextField defaultValue="" label={label} select size="small" {...registration}>
      <MenuItem value="">{t('common.all')}</MenuItem>
      <MenuItem value="ACTIVE">ACTIVE</MenuItem>
      <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
      <MenuItem value="CLOSED">CLOSED</MenuItem>
    </TextField>
  );
}

function MasterDetail({
  dataSource,
  kind,
}: {
  readonly dataSource: DataSource;
  readonly kind: 'gameType' | 'game';
}) {
  const repository = useRepository(gamesRepositoryToken);
  const auth = useAuth();
  const { gameId = '', gameTypeId = '' } = useParams();
  const id = kind === 'gameType' ? gameTypeId : gameId;
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const resource = useResource<GameTypeDetail | GameDetail>(
    (signal) =>
      kind === 'gameType' ? repository.getGameType(id, signal) : repository.getGame(id, signal),
    [id, kind, refreshKey, repository],
    () => false,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('GAME_MANAGE');
  const canViewPartners =
    auth.status === 'authenticated' && auth.principal.permissions.includes('PARTNER_VIEW');
  const refresh = () => setRefreshKey((key) => key + 1);

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toPageError(resource.error, t('games.errors.detail'))}
        onRetry={refresh}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <MasterDetailContent
            canManage={canManage}
            canViewPartners={canViewPartners}
            dataSource={dataSource}
            item={resource.data}
            kind={kind}
            language={language}
            onEdit={() => setEditOpen(true)}
            onStatus={() => setStatusOpen(true)}
          />
        )}
      </PageState>
      {resource.data === null ? null : kind === 'gameType' ? (
        <GameTypeDialog
          gameType={resource.data as GameTypeDetail}
          onClose={() => setEditOpen(false)}
          onSaved={refresh}
          open={editOpen}
        />
      ) : (
        <GameDialog
          game={resource.data as GameDetail}
          onClose={() => setEditOpen(false)}
          onSaved={refresh}
          open={editOpen}
        />
      )}
      {resource.data === null ? null : (
        <GameStatusDialog
          onClose={() => setStatusOpen(false)}
          onSaved={refresh}
          open={statusOpen}
          target={
            kind === 'gameType'
              ? { kind, item: resource.data as GameTypeDetail }
              : { kind, item: resource.data as GameDetail }
          }
        />
      )}
    </Box>
  );
}

function MasterDetailContent({
  canManage,
  canViewPartners,
  dataSource,
  item,
  kind,
  language,
  onEdit,
  onStatus,
}: {
  readonly canManage: boolean;
  readonly canViewPartners: boolean;
  readonly dataSource: DataSource;
  readonly item: GameTypeDetail | GameDetail;
  readonly kind: 'gameType' | 'game';
  readonly language: TimeLanguage;
  readonly onEdit: () => void;
  readonly onStatus: () => void;
}) {
  const { t } = useTranslation();
  const created = formatTimestamp(item.createdAt, 'Asia/Bangkok', language);
  const updated = formatTimestamp(item.updatedAt, 'Asia/Bangkok', language);
  const game = kind === 'game' ? (item as GameDetail) : null;
  return (
    <Stack spacing={2}>
      <DetailHeader
        actions={
          canManage ? (
            <>
              <Button onClick={onEdit} variant="outlined">
                {t(kind === 'gameType' ? 'games.actions.editGameType' : 'games.actions.editGame')}
              </Button>
              <Button
                color="warning"
                disabled={item.status === 'CLOSED'}
                onClick={onStatus}
                variant="contained"
              >
                {t('games.actions.changeStatus')}
              </Button>
            </>
          ) : undefined
        }
        backTo={paths.games}
        dataSource={dataSource}
        subtitle={item.name}
        title={item.code}
      />
      <Paper component="section" variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
          {t('games.detail.overview')}
        </Typography>
        <MetadataList
          items={[
            ...(game === null
              ? []
              : [
                  {
                    label: t('games.fields.gameType'),
                    value: (
                      <MuiLink component={Link} to={`/game-types/${game.gameType.id}`}>
                        {game.gameType.code}
                      </MuiLink>
                    ),
                  },
                ]),
            { label: t('games.fields.name'), value: item.name },
            { label: t('games.fields.status'), value: <MasterStatusChip status={item.status} /> },
            {
              label: t('games.fields.effectiveStatus'),
              value: t('games.detail.effectiveValue', { status: item.effectiveStatus }),
            },
            {
              label: t('games.fields.dependencies'),
              value:
                item.dependencyBlockers.length === 0
                  ? t('games.detail.noDependencies')
                  : item.dependencyBlockers.join(', '),
            },
            {
              label: t('games.fields.createdAt'),
              value: `${created.display} (${created.originalOffset})`,
            },
            {
              label: t('games.fields.updatedAt'),
              value: `${updated.display} (${updated.originalOffset})`,
            },
          ]}
        />
      </Paper>
      {game === null || !canViewPartners ? null : (
        <Paper
          component="nav"
          aria-label={t('games.detail.related')}
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Typography component="h2" variant="h5" sx={{ mb: 1 }}>
            {t('games.detail.partnerAvailability')}
          </Typography>
          <MuiLink component={Link} to={paths.partners}>
            {t('games.detail.choosePartnerAvailability')}
          </MuiLink>
        </Paper>
      )}
    </Stack>
  );
}

function MasterStatusChip({ status }: { readonly status: MasterStatus }) {
  return (
    <Chip
      color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}
      label={status}
      size="small"
    />
  );
}
